// import {useResumeStore} from "../store/resumeStore"
export function getEmptyResume() {
  return {
    name: "",
    email: "",
    phone: "",
    location: "",
    roleTitle: "",
    role: "",
    relevantExperience: "",
    totalExperience: "",
    summary: "",
    objective: "",
    photo: "",
    photoSource: "",

    // Experience
    experience: [
      {
        company: "",
        role: "",
        project: "",
        projectDisplayMode: "",
        startDate: "",
        endDate: "",
        years: "",
        description: "",
      },
    ],
    skillGroups: {
      skills: [],
    },
    languages: [],

    education: [],
    achievements: "",
  };
}

export const shouldShowParseWarning = (data) => {
  if (!data) return false;

  const emptyTemplate = getEmptyResume();

  return JSON.stringify(data) !== JSON.stringify(emptyTemplate);
};

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function firstTextValue(...values) {
  return values.find((value) => hasText(value)) || "";
}

const MERGEABLE_WORD_SUFFIXES = new Set([
  "able",
  "al",
  "ance",
  "ant",
  "ary",
  "ate",
  "ation",
  "ed",
  "ence",
  "ent",
  "er",
  "ers",
  "ful",
  "ible",
  "ing",
  "ion",
  "ions",
  "ise",
  "ity",
  "ive",
  "ize",
  "less",
  "ly",
  "ment",
  "ments",
  "ness",
  "or",
  "ors",
  "ous",
  "ship",
  "tion",
  "tions",
  "ure",
  "ward",
  "wards",
  "work",
]);

const COMMON_SPLIT_WORDS = new Set([
  "achievement",
  "achievements",
  "analysis",
  "application",
  "applications",
  "architecture",
  "automation",
  "collaboration",
  "communication",
  "consulting",
  "coordination",
  "customer",
  "dashboard",
  "delivery",
  "deployment",
  "description",
  "developer",
  "development",
  "education",
  "engineering",
  "enterprise",
  "environment",
  "experience",
  "implementation",
  "integration",
  "management",
  "operations",
  "performance",
  "platform",
  "presentation",
  "professional",
  "project",
  "projects",
  "recognition",
  "responsibilities",
  "responsibility",
  "solutions",
  "specialist",
  "support",
  "technologies",
  "technology",
  "workflow",
]);

const PLACEHOLDER_NOISE_REGEX = /^(?:test|n\/a|na|null|undefined|sdfghj|jhgjh|asdfg?h?|qwerty|lorem\s+ipsum)$/i;

function stripHtml(value) {
  if (typeof value !== "string") return "";
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function shouldMergeWordFragments(left, right) {
  const safeLeft = String(left || "").toLowerCase();
  const safeRight = String(right || "").toLowerCase();

  if (!safeLeft || !safeRight) return false;
  if (!/^[a-z]+$/.test(safeLeft) || !/^[a-z]+$/.test(safeRight)) return false;

  if (COMMON_SPLIT_WORDS.has(`${safeLeft}${safeRight}`)) {
    return true;
  }

  return safeLeft.length >= 4 &&
    safeRight.length <= 5 &&
    MERGEABLE_WORD_SUFFIXES.has(safeRight);
}

function repairSplitWordsInText(value) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  const tokens = normalized.split(" ").filter(Boolean);

  if (tokens.length <= 1) {
    return normalized;
  }

  const mergedTokens = [tokens[0]];

  for (let i = 1; i < tokens.length; i++) {
    const currentToken = tokens[i];
    const previousToken = mergedTokens[mergedTokens.length - 1];
    const previousLetters = (previousToken.match(/[A-Za-z]+$/) || [""])[0];
    const currentLetters = (currentToken.match(/^[A-Za-z]+/) || [""])[0];

    if (shouldMergeWordFragments(previousLetters, currentLetters)) {
      mergedTokens[mergedTokens.length - 1] = `${previousToken}${currentToken}`;
    } else {
      mergedTokens.push(currentToken);
    }
  }

  return mergedTokens.join(" ");
}

function isPlaceholderNoise(value) {
  return PLACEHOLDER_NOISE_REGEX.test(String(value || "").trim());
}

function getLeadingWord(value) {
  return (String(value || "").trim().match(/^[A-Za-z]+/) || [""])[0];
}

function getTrailingWord(value) {
  const words = String(value || "").trim().match(/[A-Za-z]+/g) || [];
  return words[words.length - 1] || "";
}

function joinImportedSegments(previous, current) {
  const safePrevious = String(previous || "").trim();
  const safeCurrent = String(current || "").trim();

  if (!safePrevious) return safeCurrent;
  if (!safeCurrent) return safePrevious;

  const previousWord = getTrailingWord(safePrevious);
  const currentWord = getLeadingWord(safeCurrent);

  if (shouldMergeWordFragments(previousWord, currentWord)) {
    return `${safePrevious.slice(0, safePrevious.length - previousWord.length)}${previousWord}${safeCurrent.slice(currentWord.length)}`
      .replace(/\s+/g, " ")
      .trim();
  }

  return `${safePrevious}${/[-–—/]$/.test(safePrevious) ? "" : " "}${safeCurrent}`
    .replace(/\s+/g, " ")
    .trim();
}

function sanitizeImportedField(value) {
  const normalized = repairSplitWordsInText(normalizeImportedText(value))
    .replace(/^(?:\+\s*){1,}/, "")
    .replace(/^(?:[-*•]\s*){2,}/, "• ")
    .trim();
  return isPlaceholderNoise(normalized) ? "" : normalized;
}

function firstEducationValue(...values) {
  for (const value of values) {
    const normalized = sanitizeImportedField(value);
    if (normalized) return normalized;
  }

  return "";
}

function normalizeImportedEducation(edu = {}) {
  const accreditation = edu.accreditation || {};
  const dates = edu.dates || {};
  const grade = edu.grade || {};

  return {
    institution: firstEducationValue(
      edu.institution,
      edu.organization,
      edu.university,
      edu.college,
      edu.school,
      edu.schoolName,
      edu.collegeName,
      edu.universityName,
      edu.board,
      edu.name,
      accreditation.organization,
    ),
    url: firstEducationValue(edu.url, edu.website),
    degree: firstEducationValue(
      edu.degree,
      edu.course,
      edu.qualification,
      edu.title,
      accreditation.education,
      accreditation.name,
      edu.description,
    ),
    field: firstEducationValue(
      edu.field,
      edu.major,
      edu.specialization,
      edu.area,
      edu.studyField,
    ),
    from: firstEducationValue(
      edu.from,
      edu.startDate,
      dates.startDate,
      edu.startYear,
    ),
    to: firstEducationValue(
      edu.to,
      edu.endDate,
      dates.endDate,
      edu.endYear,
      edu.passingYear,
    ),
    years: firstEducationValue(edu.years, edu.year, edu.duration, edu.date),
    score: firstEducationValue(
      edu.score,
      grade.value,
      edu.gpa,
      edu.cgpa,
      edu.percentage,
    ),
  };
}

function looksLikeCompanyField(value) {
  return /(\bpvt\.?\s*ltd\b|\bprivate limited\b|\binc\.?\b|\bllc\.?\b|\bcorp\.?\b|\bcompany\b|\bconsulting\b|\bsolutions\b|\btechnologies\b|\bsystems\b|\bservices\b|\benterprises?\b|\bgroup\b)/i.test(
    value || ""
  );
}

function looksLikeRoleField(value) {
  return /(developer|engineer|consultant|manager|lead|architect|analyst|intern|specialist|coordinator|administrator|executive|designer|officer|assistant)/i.test(
    value || ""
  );
}

function extractDateRangeFromText(value) {
  const safeValue = sanitizeImportedField(value);
  if (!safeValue) {
    return { value: "", startDate: "", endDate: "", years: "" };
  }

  const cleanupMatchedRange = (matchedText, startDate, endDate) => {
    return {
      value: safeValue
        .replace(matchedText, " ")
        .replace(/[|,:;/-]+$/g, "")
        .replace(/\s+/g, " ")
        .trim(),
      startDate,
      endDate,
      years: matchedText.replace(/\s+/g, " ").trim().replace(/present/i, "Present"),
    };
  };

  const monthMatch = safeValue.match(
    /((Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{4})\s*[–—-]\s*(Present|present|(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{4})/i
  );
  if (monthMatch) {
    return cleanupMatchedRange(
      monthMatch[0],
      monthMatch[1],
      /present/i.test(monthMatch[3]) ? "Present" : monthMatch[3]
    );
  }

  const yearMatch = safeValue.match(/\b(\d{4})\s*[–—-]\s*(Present|present|\d{4})\b/i);
  if (yearMatch) {
    return cleanupMatchedRange(
      yearMatch[0],
      yearMatch[1],
      /present/i.test(yearMatch[2]) ? "Present" : yearMatch[2]
    );
  }

  const numericMatch = safeValue.match(
    /\b(\d{2})[/-](\d{4})\s*[–—-]\s*(Present|\d{2}[/-]\d{4})\b/i
  );
  if (numericMatch) {
    return cleanupMatchedRange(
      numericMatch[0],
      `${numericMatch[1].padStart(2, "0")}/${numericMatch[2]}`,
      /present/i.test(numericMatch[3])
        ? "Present"
        : numericMatch[3].replace("-", "/")
    );
  }

  return { value: safeValue, startDate: "", endDate: "", years: "" };
}

function normalizeImportedText(value) {
  if (typeof value !== "string") return "";

  return repairSplitWordsInText(
    value
    .replace(/\r/g, "")
    .replace(/\u00a0/g, " ")
    .replace(/([A-Za-z0-9])[-–—]\s*\n\s*([A-Za-z0-9])/g, "$1-$2")
    .replace(/\n{3,}/g, "\n\n")
  )
    .trim();
}

function normalizeImportedListItem(value) {
  return normalizeImportedText(value)
    .replace(/^(?:\+\s*){2,}/, "+ ")
    .replace(/^(?:[-*•]\s*){2,}/, "• ")
    .replace(/^[-*•]\s+/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function collectTechnologyValues(value) {
  const normalized = sanitizeImportedField(value);
  if (!normalized) return [];

  return normalized
    .split(/,|\/|\||;|•/g)
    .map((item) => sanitizeImportedField(item))
    .filter(Boolean);
}

function splitTechnologySentence(sentence) {
  const normalized = sanitizeImportedField(sentence);
  const match = normalized.match(/^(.*?)(?:\b(?:technologies?|environment)\s*:\s*(.+))$/i);
  if (!match) {
    return {
      descriptionText: normalized,
      technologies: [],
    };
  }

  return {
    descriptionText: normalized,
    technologies: collectTechnologyValues(match[2]),
  };
}

function buildQuillListHtml(items) {
  const listItems = items
    .map((item) => normalizeImportedListItem(item))
    .filter(Boolean)
    .map((item) => `<li>${escapeHtml(item)}</li>`);

  return listItems.length ? `<ul>${listItems.join("")}</ul>` : "";
}

function buildQuillParagraphHtml(value) {
  const text = normalizeImportedText(value);
  if (!text) return "";

  return `<p>${escapeHtml(text).replace(/\n+/g, "<br />")}</p>`;
}

export function isResumeEmpty(data) {
  if (!data || typeof data !== "object") return true;

  if (
    hasText(data.name) ||
    hasText(data.email) ||
    hasText(data.phone) ||
    hasText(data.location) ||
    hasText(data.roleTitle) ||
    hasText(data.role) ||
    hasText(data.summary) ||
    hasText(data.objective)
  ) {
    return false;
  }

  if (
    data.relevantExperience != null &&
    String(data.relevantExperience).trim() !== ""
  ) {
    return false;
  }

  if (data.totalExperience != null && String(data.totalExperience).trim() !== "") {
    return false;
  }

  if (Array.isArray(data.skillGroups?.skills)) {
    const hasSkills = data.skillGroups.skills.some(
      (skill) => typeof skill === "string" && skill.trim() !== ""
    );
    if (hasSkills) return false;
  }

  if (Array.isArray(data.languages)) {
    const hasLanguages = data.languages.some(
      (language) => typeof language === "string" && language.trim() !== ""
    );
    if (hasLanguages) return false;
  }

  if (Array.isArray(data.education)) {
    const hasEducation = data.education.some(
      (edu) =>
        hasText(edu?.institution) ||
        hasText(edu?.degree) ||
        hasText(edu?.from) ||
        hasText(edu?.to)
    );
    if (hasEducation) return false;
  }

  if (Array.isArray(data.experience)) {
    const hasExperience = data.experience.some((exp) => {
      const descriptionText = hasText(exp?.description)
        ? stripHtml(exp.description)
        : "";
      return (
        hasText(exp?.company) ||
        hasText(exp?.role) ||
        hasText(exp?.project) ||
        hasText(exp?.startDate) ||
        hasText(exp?.endDate) ||
        hasText(exp?.years) ||
        descriptionText.length > 0
      );
    });
    if (hasExperience) return false;
  }

  if (hasText(stripHtml(data.achievements))) return false;

  return true;
}

function buildBulletsFromBrokenLines(descriptionArray) {
  if (!Array.isArray(descriptionArray)) return [];

  const mergedSentences = [];
  let buffer = "";

  for (let i = 0; i < descriptionArray.length; i++) {
    const line = sanitizeImportedField(descriptionArray[i]);
    if (!line) continue;
    if (isPlaceholderNoise(line)) continue;

    // Add line to buffer
    buffer = buffer ? joinImportedSegments(buffer, line) : line;

    // If line ends with period, sentence is complete
    if (line.endsWith(".")) {
      mergedSentences.push(buffer.trim());
      buffer = "";
    }
  }

  // If something left in buffer without period
  if (buffer) {
    mergedSentences.push(buffer.trim());
  }

  return mergedSentences;
}

function cleanResumeSummary(text) {

  if (!text) return "";

  text = text.toLowerCase();

  return text
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")

    // split merged lowercase words
    .replace(/([a-z]{4,})([A-Z])/g, "$1 $2")

    // fix common resume keywords
    .replace(/workedacross/g, "worked across")
    .replace(/sdlcfrom/g, "sdlc from")
    .replace(/requirementsand/g, "requirements and")
    .replace(/analysisand/g, "analysis and")
    .replace(/yrstotal/g, "yrs total")

    // fix html/css spacing
    .replace(/html ?5/g, "html5")
    .replace(/css ?3/g, "css3")

    .replace(/\s{2,}/g, " ")
    .trim();
}

export function mapApiResumeToEditor(apiResponse) {
  const resume = getEmptyResume();
  const apiData =
    apiResponse?.parsedData ||
    apiResponse?.rawApiResponse?.data ||
    apiResponse ||
    {};

  /* ===================================================
     PERSONAL DETAILS
  =================================================== */

  resume.name = (apiData.name || "").toString().trim();

  resume.email = (apiData.email || "").toString().trim();

  // Clean phone (remove newline, spaces)
  resume.phone = (apiData.phone || "")
    .toString()
    .replace(/\s+/g, "")
    .replace(/[^\d+]/g, "");

  // Prevent email accidentally being used as location
  resume.location =
    apiData.location &&
    apiData.location !== apiData.email
      ? apiData.location
      : "";

  const inferredRole =
    apiData.roleTitle ||
    apiData.role ||
    (Array.isArray(apiData.experience) ? apiData.experience[0]?.role : "") ||
    "";

  resume.roleTitle = inferredRole;
  resume.role = inferredRole;

  resume.relevantExperience =
  apiData.relevantExperience != null
    ? String(apiData.relevantExperience)
    : "";

resume.totalExperience =
  apiData.totalExperience != null
    ? String(apiData.totalExperience)
    : "";

  resume.summary = cleanResumeSummary(
    firstTextValue(
      apiData.summary,
      apiData.aboutMe,
      apiData.about,
      apiData.professionalSummary,
      apiData.profileSummary,
      apiData.profile,
    ),
  );

  resume.objective = firstTextValue(
    apiData.objective,
    apiData.careerObjective,
    apiData.career_objective,
    apiData.careerSummary,
    apiData.careerGoal,
    apiData.goal,
  ).toString().trim();

  /* ===================================================
     SKILLS
  =================================================== */

  if (Array.isArray(apiData.skills)) {
    resume.skillGroups.skills = Array.from(
      new Set(
        apiData.skills
          .filter((s) => typeof s === "string")
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
  } else if (
    apiData.skillGroups &&
    Array.isArray(apiData.skillGroups.skills)
  ) {
    resume.skillGroups.skills = Array.from(
      new Set(
        apiData.skillGroups.skills
          .filter((s) => typeof s === "string")
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
  }

  if (Array.isArray(apiData.languages)) {
    resume.languages = Array.from(
      new Set(
        apiData.languages
          .map((language) => {
            if (typeof language === "string") return language.trim();
            if (language && typeof language === "object") {
              return (
                language.name ||
                language.language ||
                language.value ||
                language.title ||
                ""
              ).toString().trim();
            }
            return "";
          })
          .filter(Boolean)
      )
    );
  } else if (typeof apiData.languages === "string") {
    resume.languages = Array.from(
      new Set(
        apiData.languages
          .split(/,|\n/)
          .map((language) => language.trim())
          .filter(Boolean)
      )
    );
  } else if (typeof apiData.language === "string") {
    resume.languages = Array.from(
      new Set(
        apiData.language
          .split(/,|\n/)
          .map((language) => language.trim())
          .filter(Boolean)
      )
    );
  }

  /* ===================================================
     EXPERIENCE
  =================================================== */

  const experienceSource = Array.isArray(apiData?.experience)
    ? apiData.experience
    : Array.isArray(apiData?.responsibilities)
    ? apiData.responsibilities
    : [];

  if (experienceSource.length > 0) {
    resume.experience = experienceSource
      .filter((exp) => exp && typeof exp === "object")
      .map((exp) => {
        let startDate = sanitizeImportedField(exp.startDate);
        let endDate = sanitizeImportedField(exp.endDate);
        let years = sanitizeImportedField(exp.years);
        let company = sanitizeImportedField(exp.company);
        let role = sanitizeImportedField(exp.role);
        let project = sanitizeImportedField(exp.project);
        const location = sanitizeImportedField(exp.location);

        const monthMap = {
          "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
          "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
          "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec"
        };

        let descriptionArray = Array.isArray(exp.description)
          ? [...exp.description]
          : Array.isArray(exp.responsibilities)
          ? [...exp.responsibilities]
          : typeof exp.description === "string"
          ? normalizeImportedText(exp.description).split(/\n+/)
          : [];

        const companyDate = extractDateRangeFromText(company);
        const roleDate = extractDateRangeFromText(role);
        const projectDate = extractDateRangeFromText(project);

        if (!startDate && !endDate && !years) {
          const durationMeta =
            companyDate.years ? companyDate :
            roleDate.years ? roleDate :
            projectDate.years ? projectDate :
            null;

          if (durationMeta) {
            startDate = durationMeta.startDate;
            endDate = durationMeta.endDate;
            years = durationMeta.years;
          }
        }

        company = companyDate.value;
        role = roleDate.value;
        project = projectDate.value;

        if (company && role && looksLikeRoleField(company) && looksLikeCompanyField(role)) {
          [company, role] = [role, company];
        }

        if (!years && descriptionArray.length > 0) {
          const brokenRegex = /(\d{4})-(\d{2})(\d{4})-(\d{2})/;

          for (let i = 0; i < descriptionArray.length; i++) {
            const match = descriptionArray[i].match(brokenRegex);

            if (match) {
              const [, startYear, startMonth, endYear, endMonth] = match;
              startDate = `${monthMap[startMonth]} ${startYear}`;
              endDate = `${monthMap[endMonth]} ${endYear}`;
              years = `${startDate} - ${endDate}`;
              descriptionArray.splice(i, 1);
              break;
            }
          }
        }

        if (!years && descriptionArray.length > 0) {
          for (let i = 0; i < descriptionArray.length; i++) {
            const durationMeta = extractDateRangeFromText(descriptionArray[i]);
            if (!durationMeta.years) continue;

            startDate = durationMeta.startDate;
            endDate = durationMeta.endDate;
            years = durationMeta.years;

            if (durationMeta.value) {
              descriptionArray[i] = durationMeta.value;
            } else {
              descriptionArray.splice(i, 1);
            }

            break;
          }
        }

        if (!years && typeof exp.duration === "string" && exp.duration.includes("-")) {
          const parts = exp.duration.split("-");
          startDate = parts[0]?.trim() || "";
          endDate = parts[1]?.trim() || "";
          years = `${startDate} - ${endDate}`;
        }

        let descriptionHtml = "";
        let technologies = Array.isArray(exp.technologies)
          ? exp.technologies.flatMap((item) => collectTechnologyValues(item))
          : [];

        technologies = [
          ...technologies,
          ...collectTechnologyValues(exp.technology),
          ...collectTechnologyValues(exp.techStack),
          ...collectTechnologyValues(exp.tools),
          ...collectTechnologyValues(exp.skills),
        ];

        if (descriptionArray.length > 0) {
          const sentences = buildBulletsFromBrokenLines(descriptionArray);
          const descriptionSentences = [];

          sentences.forEach((sentence) => {
            const technologySplit = splitTechnologySentence(sentence);
            if (technologySplit.technologies.length > 0) {
              technologies.push(...technologySplit.technologies);

              if (technologySplit.descriptionText) {
                descriptionSentences.push(technologySplit.descriptionText);
              }
              return;
            }

            descriptionSentences.push(technologySplit.descriptionText || sentence);
          });

          descriptionHtml = buildQuillListHtml(
            descriptionSentences.filter(
              (sentence) => sentence && sentence.trim().length > 2,
            )
          );
        }

        return {
          company,
          role,
          project,
          projectDisplayMode: "",
          location,
          startDate,
          endDate,
          years,
          description: descriptionHtml,
          technologies: Array.from(
            new Set(
              technologies
                .map((item) => sanitizeImportedField(item))
                .filter(Boolean),
            ),
          ),
        };
      })
      .filter((exp) => exp.company || exp.role);
  }
  /* ===================================================
     EDUCATION
  =================================================== */

  if (Array.isArray(apiData.education)) {
    resume.education = apiData.education
      .map(normalizeImportedEducation)
      .filter(
        (edu) =>
          edu.institution ||
          edu.degree ||
          edu.field ||
          edu.from ||
          edu.to ||
          edu.years ||
          edu.score,
      );
  }

  /* ===================================================
     ACHIEVEMENTS
  =================================================== */

  if (Array.isArray(apiData.achievements)) {
    const items = apiData.achievements
      .filter((a) => typeof a === "string")
      .map((a) => normalizeImportedListItem(a))
      .filter(Boolean);

    resume.achievements = buildQuillListHtml(items);
  } else if (typeof apiData.achievements === "string") {
    const value = apiData.achievements.trim();
    if (/<[a-z][\s\S]*>/i.test(value)) {
      resume.achievements = value;
    } else if (/\n|^[-*•]\s/m.test(value)) {
      const items = normalizeImportedText(value)
        .split(/\n+/)
        .map((item) => normalizeImportedListItem(item))
        .filter(Boolean);
      resume.achievements = buildQuillListHtml(items);
    } else {
      resume.achievements = buildQuillParagraphHtml(value);
    }
  } else {
    resume.achievements = "";
  }

  return resume;
}
